import Phaser from 'phaser';
import { Unit, UnitConfig } from '../base/Unit';
import { FlyerBehavior } from '../behaviors/FlyerBehavior';
import { Allegiance } from '../../constants/enums';

export interface BossBatConfig extends UnitConfig {
  chaseSpeed?: number;
  hoverAmplitude?: number;
  hoverSpeed?: number;
  damage?: number;
}

/**
 * BossBat - Small bat spawned by the Cyber Scarecrow boss
 *
 * Behavior:
 * - Flies with sinusoidal movement toward the player
 * - Damages player on contact
 * - Has 2 HP, can be shot down
 */
export class BossBat extends Unit {
  private flyer: FlyerBehavior;
  private chaseSpeed: number;
  private damage: number;
  private hoverOffset: number = 0;
  private hoverTime: number = 0;
  private hoverAmplitude: number;
  private hoverSpeed: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string,
    config: BossBatConfig = {}
  ) {
    super(scene, x, y, texture, {
      ...config,
      hp: config.hp ?? 2,
      allegiance: Allegiance.Enemy,
    });

    this.chaseSpeed = config.chaseSpeed ?? 150;
    this.damage = config.damage ?? 1;
    this.hoverAmplitude = config.hoverAmplitude ?? 20;
    this.hoverSpeed = config.hoverSpeed ?? 5;

    // Add flyer behavior (disables gravity)
    this.flyer = this.addBehavior(new FlyerBehavior(this, {
      speed: this.chaseSpeed,
      hoverAmplitude: 0, // We handle sinusoidal movement ourselves
      hoverSpeed: this.hoverSpeed,
    }));

    // Random starting phase for hover
    this.hoverTime = Math.random() * Math.PI * 2;

    // Size is set by EntityFactory from override data (default ~64x64)
    // Don't hardcode size here - let config drive it

    // Physics body - size will be set by EntityFactory.applyHitbox()
    if (this.body) {
      const body = this.body as Phaser.Physics.Arcade.Body;
      body.setAllowGravity(false);
    }
  }

  /**
   * Update - chase the player with sinusoidal movement
   */
  update(time: number, delta: number): void {
    super.update(time, delta);

    if (this.isDead()) return;

    // Get player reference
    const player = this.scene.registry.get('player') as Phaser.GameObjects.Sprite | undefined;
    if (!player || !player.active) return;

    // Calculate direction to player
    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Check for collision with player (simple distance check)
    const collisionRadius = 20;
    if (distance < collisionRadius) {
      this.onPlayerCollision();
      return;
    }

    if (distance > 0) {
      // Normalize direction
      const dirX = dx / distance;
      const dirY = dy / distance;

      // Add sinusoidal perpendicular movement for wavy flight
      this.hoverTime += delta / 1000 * this.hoverSpeed;
      const perpX = -dirY; // Perpendicular to movement direction
      const perpY = dirX;
      const sineOffset = Math.sin(this.hoverTime) * this.hoverAmplitude;

      // Calculate velocity with sine wave offset
      const body = this.body as Phaser.Physics.Arcade.Body;
      if (body) {
        const targetVelX = dirX * this.chaseSpeed + perpX * sineOffset * 2;
        const targetVelY = dirY * this.chaseSpeed + perpY * sineOffset * 2;

        // Smooth velocity transition
        body.setVelocity(targetVelX, targetVelY);
      }

      // Flip sprite based on movement direction
      this.setFlipX(dx < 0);
    }
  }

  /**
   * Called when bat collides with player
   */
  onPlayerCollision(): void {
    // Get player and deal damage directly
    const player = this.scene.registry.get('player') as { takeDamage?: (amount: number) => void } | undefined;
    if (player && player.takeDamage) {
      player.takeDamage(this.damage);
    }

    // Bat dies on contact
    this.takeDamage(this.hp, 'contact');
  }

  /**
   * Get the damage this bat deals
   */
  getDamage(): number {
    return this.damage;
  }
}
